import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Facebook, Linkedin, Loader2, Mail, Twitter } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Overline } from "@/components/Reveal";

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

export default function ArticlePost() {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [missing, setMissing] = useState(false);

  useSEO(
    item ? `${item.title} | Terrain and Truth` : "Terrain and Truth | Rooted Healing",
    item?.excerpt || item?.description || "An article from Terrain and Truth by Staci Freed."
  );

  useEffect(() => {
    api
      .get(`/content/media/${itemId}`)
      .then(({ data }) => setItem(data))
      .catch(() => setMissing(true));
  }, [itemId]);

  if (missing) {
    return (
      <main className="mx-auto max-w-2xl px-5 pb-24 pt-[140px] text-center">
        <p className="font-serif text-2xl text-forest">This article is no longer available.</p>
        <Link to="/terrain-and-truth" className="mt-4 inline-block text-sm font-semibold text-gold-dark underline-offset-4 hover:underline">
          Back to Terrain and Truth
        </Link>
      </main>
    );
  }

  if (!item) {
    return (
      <main className="flex min-h-screen items-center justify-center pt-[76px]">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </main>
    );
  }

  const shareUrl = window.location.href;
  const paragraphs = (item.body || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <main data-testid="article-page" className="pt-[76px]">
      <article className="mx-auto max-w-3xl px-5 py-14 sm:px-8">
        <Link
          to="/terrain-and-truth"
          className="inline-flex items-center gap-2 text-sm font-semibold text-forest underline-offset-4 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Terrain and Truth
        </Link>
        <div className="mt-8">
          <Overline>Posts & Articles</Overline>
        </div>
        <h1 className="mt-4 font-serif text-4xl leading-tight text-forest sm:text-5xl">
          {item.title}
        </h1>
        <p className="mt-3 text-sm text-taupe">{fmt(item.published_at)}</p>
        {item.featured_image && (
          <img
            src={item.featured_image}
            alt={`${item.title} featured image`}
            className="mt-8 w-full rounded-[2rem] object-cover shadow-[0_20px_50px_rgba(23,63,53,0.12)]"
          />
        )}
        <div className="mt-8 flex flex-col gap-5">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-lg font-light leading-relaxed text-charcoal/80">
              {p}
            </p>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-forest/10 pt-7">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-taupe">
            Share this article
          </span>
          <a
            data-testid="share-facebook"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-forest/20 text-forest transition-colors hover:border-gold"
          >
            <Facebook className="h-4 w-4" />
          </a>
          <a
            data-testid="share-twitter"
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(item.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on X"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-forest/20 text-forest transition-colors hover:border-gold"
          >
            <Twitter className="h-4 w-4" />
          </a>
          <a
            data-testid="share-linkedin"
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-forest/20 text-forest transition-colors hover:border-gold"
          >
            <Linkedin className="h-4 w-4" />
          </a>
          <a
            data-testid="share-email"
            href={`mailto:?subject=${encodeURIComponent(item.title)}&body=${encodeURIComponent(shareUrl)}`}
            aria-label="Share by email"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-forest/20 text-forest transition-colors hover:border-gold"
          >
            <Mail className="h-4 w-4" />
          </a>
        </div>
        <p className="mt-8 text-xs leading-relaxed text-taupe">
          Educational content from Rooted Healing by Staci Freed. It is not medical advice.
        </p>
      </article>
    </main>
  );
}
